import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const QUICK = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "ytd", label: "YTD" },
];

const MORE = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "14d", label: "Last 14 days" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "12m", label: "Last 12 months" },
  { value: "all", label: "All time" },
];

const PRESET_LABELS = Object.fromEntries(
  [...QUICK, ...MORE].map((p) => [p.value, p.label]),
);

const PLATFORMS = [
  { value: "all", label: "All" },
  { value: "web", label: "Web" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
];

const COMPARE = [
  { value: "prior", label: "Prior period" },
  { value: "yoy", label: "Last year" },
];


function customLabel(start, end) {
  if (!start || !end) return "Custom range";
  return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
}


export default function Filters({
  preset,
  onPresetChange,
  customStart,
  customEnd,
  onCustomRangeChange,
  platform,
  onPlatformChange,
  compareTo,
  onCompareToChange,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draft, setDraft] = useState(
    customStart && customEnd
      ? { from: new Date(customStart), to: new Date(customEnd) }
      : undefined,
  );

  const isCustom = preset === "custom";
  const activeQuick = QUICK.find((q) => q.value === preset)?.value;
  const morePresetActive = MORE.some((m) => m.value === preset);
  const morePresetLabel = morePresetActive ? PRESET_LABELS[preset] : "More";

  const handleApplyCustom = () => {
    if (!draft?.from || !draft?.to) return;
    onCustomRangeChange(
      format(draft.from, "yyyy-MM-dd"),
      format(draft.to, "yyyy-MM-dd"),
    );
    setPickerOpen(false);
  };

  const handlePresetClick = (v) => {
    if (v === preset) return;
    onPresetChange(v);
  };

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-medium hidden md:inline mr-1">
          Date range
        </span>

        {QUICK.map((q) => (
          <Button
            key={q.value}
            size="sm"
            variant={activeQuick === q.value ? "default" : "outline"}
            className="h-8 px-3 text-xs"
            onClick={() => handlePresetClick(q.value)}
            data-testid={`filter-date-${q.value}`}
          >
            {q.label}
          </Button>
        ))}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant={morePresetActive ? "default" : "outline"}
              className="h-8 px-3 text-xs gap-1"
              data-testid="filter-date-more"
            >
              {morePresetLabel}
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
              Presets
            </DropdownMenuLabel>
            {MORE.map((m) => (
              <DropdownMenuItem
                key={m.value}
                onSelect={() => handlePresetClick(m.value)}
                data-testid={`filter-date-${m.value}`}
                className="text-sm"
              >
                {m.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant={isCustom ? "default" : "outline"}
              className="h-8 px-3 text-xs gap-1.5"
              data-testid="filter-date-custom"
            >
              <CalendarIcon className="h-3.5 w-3.5" />
              {isCustom && customStart && customEnd
                ? customLabel(new Date(customStart), new Date(customEnd))
                : "Custom…"}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-auto p-0"
            data-testid="filter-date-custom-popover"
          >
            <Calendar
              initialFocus
              mode="range"
              numberOfMonths={2}
              defaultMonth={draft?.from}
              selected={draft}
              onSelect={setDraft}
              disabled={(d) => d > new Date()}
            />
            <div className="flex items-center justify-between border-t border-border p-3 gap-2 text-xs">
              <span className="text-muted-foreground" data-testid="custom-range-preview">
                {draft?.from && draft?.to
                  ? customLabel(draft.from, draft.to)
                  : "Pick a start and end date"}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-3 text-xs"
                  onClick={() => {
                    setDraft(undefined);
                    setPickerOpen(false);
                  }}
                  data-testid="custom-range-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-7 px-3 text-xs"
                  disabled={!draft?.from || !draft?.to}
                  onClick={handleApplyCustom}
                  data-testid="custom-range-apply"
                >
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3">
        <Tabs value={compareTo} onValueChange={onCompareToChange} className="w-full md:w-auto">
          <TabsList className="grid w-full grid-cols-2 md:w-auto md:flex" data-testid="filter-compare-tabs">
            {COMPARE.map((c) => (
              <TabsTrigger
                key={c.value}
                value={c.value}
                data-testid={`filter-compare-${c.value}`}
                className="text-xs md:text-sm"
              >
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Tabs value={platform} onValueChange={onPlatformChange} className="w-full md:w-auto">
          <TabsList className="grid w-full grid-cols-4 md:w-auto md:flex" data-testid="filter-platform-tabs">
            {PLATFORMS.map((p) => (
              <TabsTrigger
                key={p.value}
                value={p.value}
                data-testid={`filter-platform-${p.value}`}
                className="text-xs md:text-sm"
              >
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
}
